import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './print-dialog.css';

interface Props {
  documentTitle?: string;
  show: boolean;
  onClose?: () => void;
  children: React.ReactElement;
  landscape?: boolean;
  showBackgroundGraphics?: boolean;
}

/**
 * This component will render given children(print only) element on container element '#print' (with help of react portal)
 * which present as sibling of application's root container element '#root'.
 *
 * NOTE: on print, this will hide whole application(#root) and show only passed children component.
 *
 * The static print styles (hiding #root, showing #print) live in ./print-dialog.css, gated to apply
 * only while this component has portaled content into #print. The two dynamic aspects
 * (`print-color-adjust` from `showBackgroundGraphics`, and the `@page` size from `landscape`) can't be
 * expressed statically, so they are injected into a small <style> element that only lives while this
 * component is mounted — replacing the previous Emotion <Global> without depending on Emotion.
 */
export const PrintDialog = ({
  children,
  show,
  onClose,
  documentTitle,
  landscape = false,
  showBackgroundGraphics = false,
}: Props) => {
  const handleAfterPrint = () => {
    onClose && onClose();
  };

  useEffect(() => {
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, []);

  useEffect(() => {
    const colorAdjust = showBackgroundGraphics ? 'exact' : 'economy';
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        #print {
          -webkit-print-color-adjust: ${colorAdjust};
          color-adjust: ${colorAdjust};
        }

        @page {
          size: ${landscape ? 'landscape' : 'portrait'};
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [landscape, showBackgroundGraphics]);

  useEffect(() => {
    if (!show) return;

    const title = document.title;
    if (documentTitle) document.title = documentTitle;
    window.print();
    if (documentTitle) document.title = title;
  }, [show]);

  return <>{createPortal(children, document.getElementById('print') as HTMLDivElement)}</>;
};
